import React, { useState, useEffect } from 'react';
import { MdRefresh, MdDashboard, MdWarning, MdCheckCircle, MdQrCodeScanner, MdTrendingUp } from 'react-icons/md';
import { Bar, Doughnut, Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  ArcElement,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler
} from 'chart.js';
import { getUserAnalytics, getScanAnalytics } from '../../../api/vehicleAnalytics';
import '../../../styles/VehicleDashboard.css';
import './BookRoom.css';

// Register ChartJS components
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  ArcElement,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

function VehicleDashboard() {
  const [analyticsData, setAnalyticsData] = useState(null);
  const [scanData, setScanData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    loadDashboardData();
  }, []);

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      setError('');

      // Load both analytics in parallel
      const [analyticsResponse, scanResponse] = await Promise.all([
        getUserAnalytics(),
        getScanAnalytics().catch(err => {
          console.warn('Scan analytics not available:', err);
          return null;
        })
      ]);

      if (analyticsResponse?.data) {
        setAnalyticsData(analyticsResponse.data);
      } else {
        throw new Error(analyticsResponse?.message || 'Failed to load analytics data');
      }

      if (scanResponse?.data) {
        setScanData(scanResponse.data);
      }
    } catch (err) {
      console.error('Dashboard load error:', err);
      setError(err?.response?.data?.message || 'Failed to load dashboard data. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Chart configurations
  const capacityBreakdownChartData = analyticsData?.capacityBreakdown ? {
    labels: ['At Capacity', 'Over Capacity', 'Under Utilized', 'Optimal'],
    datasets: [{
      label: 'Routes',
      data: [
        analyticsData.capacityBreakdown.routesAtCapacity || 0,
        analyticsData.capacityBreakdown.routesOverCapacity || 0,
        analyticsData.capacityBreakdown.routesUnderUtilized || 0,
        analyticsData.capacityBreakdown.routesOptimal || 0
      ],
      backgroundColor: [
        'rgba(255, 206, 86, 0.6)',    // Yellow for At Capacity
        'rgba(255, 99, 132, 0.6)',    // Red for Over Capacity
        'rgba(201, 203, 207, 0.6)',   // Gray for Under Utilized
        'rgba(75, 192, 192, 0.6)',    // Green for Optimal
      ],
      borderColor: [
        'rgba(255, 206, 86, 1)',
        'rgba(255, 99, 132, 1)',
        'rgba(201, 203, 207, 1)',
        'rgba(75, 192, 192, 1)',
      ],
      borderWidth: 1,
    }]
  } : null;

  const routeCapacityChartData = analyticsData?.routeAnalytics ? {
    labels: analyticsData.routeAnalytics.map(r => r.routeName),
    datasets: [
      {
        label: 'Capacity',
        data: analyticsData.routeAnalytics.map(r => r.totalCapacity),
        backgroundColor: 'rgba(54, 162, 235, 0.6)',
      },
      {
        label: 'Assigned Users',
        data: analyticsData.routeAnalytics.map(r => r.assignedUsers),
        backgroundColor: 'rgba(75, 192, 192, 0.6)',
      }
    ]
  } : null;

  const userDistributionChartData = analyticsData?.userDistribution ? {
    labels: analyticsData.userDistribution.map(d => d.routeName),
    datasets: [{
      label: 'User Count',
      data: analyticsData.userDistribution.map(d => d.userCount),
      backgroundColor: 'rgba(153, 102, 255, 0.6)',
    }]
  } : null;

  // Scan Analytics Charts
  const dailyTrendChartData = scanData?.dailyTrend ? {
    labels: scanData.dailyTrend.map(d => {
      const date = new Date(d.date);
      return date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
    }),
    datasets: [{
      label: 'Daily Scans',
      data: scanData.dailyTrend.map(d => d.scanCount),
      fill: true,
      backgroundColor: 'rgba(99, 102, 241, 0.2)',
      borderColor: 'rgba(99, 102, 241, 1)',
      tension: 0.4,
      pointBackgroundColor: 'rgba(99, 102, 241, 1)',
    }]
  } : null;

  const routeScansChartData = scanData?.scansByRoute ? {
    labels: scanData.scansByRoute.map(r => r.route),
    datasets: [{
      label: 'Scans',
      data: scanData.scansByRoute.map(r => r.scanCount),
      backgroundColor: [
        'rgba(99, 102, 241, 0.7)',
        'rgba(236, 72, 153, 0.7)',
        'rgba(34, 197, 94, 0.7)',
        'rgba(251, 146, 60, 0.7)',
        'rgba(14, 165, 233, 0.7)',
        'rgba(168, 85, 247, 0.7)',
        'rgba(244, 63, 94, 0.7)',
        'rgba(20, 184, 166, 0.7)',
      ],
      borderWidth: 0,
    }]
  } : null;

  const timingChartData = scanData?.scansByTiming ? {
    labels: Object.keys(scanData.scansByTiming),
    datasets: [{
      label: 'Scans by Timing',
      data: Object.values(scanData.scansByTiming),
      backgroundColor: ['rgba(34, 197, 94, 0.7)', 'rgba(251, 146, 60, 0.7)'],
      borderWidth: 0,
    }]
  } : null;

  const getCapacityStatusColor = (status) => {
    switch (status) {
      case 'OVER_CAPACITY':
        return 'status-over';
      case 'AT_CAPACITY':
        return 'status-at';
      case 'OPTIMAL':
        return 'status-optimal';
      case 'UNDER_UTILIZED':
        return 'status-under';
      default:
        return '';
    }
  };

  const getCapacityStatusLabel = (status) => {
    switch (status) {
      case 'OVER_CAPACITY':
        return (
          <span>
            Over Capacity <MdWarning style={{ display: 'inline', marginLeft: '4px', color: '#ef4444' }} />
          </span>
        );
      case 'AT_CAPACITY':
        return 'At Capacity';
      case 'OPTIMAL':
        return (
          <span>
            Optimal <MdCheckCircle style={{ display: 'inline', marginLeft: '4px', color: '#10b981' }} />
          </span>
        );
      case 'UNDER_UTILIZED':
        return 'Under Utilized';
      default:
        return status;
    }
  };

  if (loading) {
    return (
      <div className="dashboard-container">
        <div className="loading-spinner">Loading dashboard...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="dashboard-container">
        <div className="error-message">{error}</div>
        <button onClick={loadDashboardData} className="retry-button">Retry</button>
      </div>
    );
  }

  return (
    <div className="vehicle-dashboard">
      {/* Compact Header */}
      <div className="page-header-compact">
        <div className="header-left-compact">
          <h2 className="page-title-compact">
            <MdDashboard className="title-icon" />
            Vehicle Analytics Dashboard
          </h2>
        </div>
        <div className="header-filters-compact">
          <button className="btn-book-compact" onClick={loadDashboardData}>
            <MdRefresh />
            <span>Refresh Data</span>
          </button>
        </div>
      </div>

      {/* Scan Analytics Section - First View */}
      {scanData && (
        <>
          <div className="scan-analytics-header">
            <h2>
              <MdQrCodeScanner className="section-icon" />
              Scan Analytics
            </h2>
          </div>

          {/* Scan Summary Cards */}
          <div className="metrics-grid">
            <div className="metric-card scan-metric">
              <h3>Today's Scans</h3>
              <p className="metric-value">{scanData.totalScansToday || 0}</p>
              <div className="metric-details">
                <span className="highlight">{scanData.uniqueUsersScannedToday || 0} unique users</span>
              </div>
            </div>

            <div className="metric-card scan-metric">
              <h3>This Week</h3>
              <p className="metric-value">{scanData.totalScansThisWeek || 0}</p>
              <div className="metric-details">
                <span>Last 7 days</span>
              </div>
            </div>

            <div className="metric-card scan-metric">
              <h3>This Month</h3>
              <p className="metric-value">{scanData.totalScansThisMonth || 0}</p>
              <div className="metric-details">
                <span>Last 30 days</span>
              </div>
            </div>

            <div className="metric-card scan-metric">
              <h3>
                <MdTrendingUp style={{ marginRight: '8px' }} />
                Avg Daily
              </h3>
              <p className="metric-value">
                {scanData.dailyTrend ? Math.round(scanData.dailyTrend.reduce((sum, d) => sum + d.scanCount, 0) / 7) : 0}
              </p>
              <div className="metric-details">
                <span>Per day average</span>
              </div>
            </div>
          </div>

          {/* Scan Charts */}
          <div className="charts-grid">
            {dailyTrendChartData && (
              <div className="chart-card wide">
                <h3>Daily Scan Trend (Last 7 Days)</h3>
                <Line
                  data={dailyTrendChartData}
                  options={{
                    responsive: true,
                    maintainAspectRatio: true,
                    plugins: {
                      legend: { display: false }
                    },
                    scales: {
                      y: {
                        beginAtZero: true,
                        ticks: { stepSize: 1 }
                      }
                    }
                  }}
                />
              </div>
            )}

            {routeScansChartData && scanData.scansByRoute?.length > 0 && (
              <div className="chart-card">
                <h3>Scans by Route (This Week)</h3>
                <Doughnut
                  data={routeScansChartData}
                  options={{
                    responsive: true,
                    maintainAspectRatio: true,
                    plugins: {
                      legend: {
                        position: 'right',
                        labels: { boxWidth: 12, padding: 10 }
                      }
                    }
                  }}
                />
              </div>
            )}

            {timingChartData && Object.keys(scanData.scansByTiming || {}).length > 0 && (
              <div className="chart-card small">
                <h3>Scans by Timing</h3>
                <Doughnut
                  data={timingChartData}
                  options={{
                    responsive: true,
                    maintainAspectRatio: true,
                  }}
                />
              </div>
            )}
          </div>

          {/* Driver Performance & Vehicle Usage */}
          <div className="scan-details-grid">
            {scanData.scansByDriver && scanData.scansByDriver.length > 0 && (
              <div className="scan-details-card">
                <h3>Driver Performance (This Week)</h3>
                <div className="scan-list">
                  {scanData.scansByDriver.map((driver, idx) => (
                    <div key={idx} className="scan-list-item">
                      <span className="scan-label">{driver.driverName}</span>
                      <span className="scan-count">{driver.scanCount} scans</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {scanData.scansByVehicle && scanData.scansByVehicle.length > 0 && (
              <div className="scan-details-card">
                <h3>Vehicle Usage (This Week)</h3>
                <div className="scan-list">
                  {scanData.scansByVehicle.map((vehicle, idx) => (
                    <div key={idx} className="scan-list-item">
                      <span className="scan-label">{vehicle.vehicleNumber}</span>
                      <span className="scan-count">{vehicle.scanCount} scans ({vehicle.percentage}%)</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Recent Scans Table */}
          {scanData.recentScans && scanData.recentScans.length > 0 && (
            <div className="route-details-section">
              <h2>Recent Scans (Today)</h2>
              <div className="table-responsive">
                <table className="route-table">
                  <thead>
                    <tr>
                      <th>Employee ID</th>
                      <th>Name</th>
                      <th>Route</th>
                      <th>Vehicle</th>
                      <th>Timing</th>
                      <th>Scan Time</th>
                      <th>Scanned By</th>
                    </tr>
                  </thead>
                  <tbody>
                    {scanData.recentScans.map((scan, index) => (
                      <tr key={index}>
                        <td>{scan.empId}</td>
                        <td><strong>{scan.employeeName}</strong></td>
                        <td>{scan.route}</td>
                        <td>{scan.vehicle}</td>
                        <td>
                          <span className={`timing-badge ${scan.timing?.toLowerCase()}`}>
                            {scan.timing}
                          </span>
                        </td>
                        <td>{scan.scanTime}</td>
                        <td>{scan.scannedBy}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </>
      )}

      {analyticsData && (
        <>
          {/* Route & Capacity Analytics Section */}
          <div className="scan-analytics-header">
            <h2>
              <MdDashboard className="section-icon" />
              Route & Capacity Analytics
            </h2>
          </div>

          {/* Summary Cards */}
          <div className="metrics-grid">
            <div className="metric-card">
              <h3>Total Routes</h3>
              <p className="metric-value">{analyticsData.totalRoutes}</p>
              <div className="metric-details">
                <span>Active routes with assigned vehicles</span>
              </div>
            </div>

            <div className="metric-card">
              <h3>Total Vehicles</h3>
              <p className="metric-value">{analyticsData.totalVehicles}</p>
              <div className="metric-details">
                <span>Capacity: {analyticsData.totalCapacity} seats</span>
              </div>
            </div>

            <div className="metric-card">
              <h3>Assigned Users</h3>
              <p className="metric-value">{analyticsData.totalAssignedUsers}</p>
              <div className="metric-details">
                <span>Users assigned to routes</span>
              </div>
            </div>

            <div className="metric-card">
              <h3>Overall Utilization</h3>
              <p className="metric-value">{analyticsData.overallUtilization?.toFixed(1)}%</p>
              <div className="metric-details">
                <span className={analyticsData.overallUtilization > 100 ? 'over-capacity' : 'normal'}>
                  {analyticsData.overallUtilization > 100 ? 'Over Capacity' :
                   analyticsData.overallUtilization >= 80 ? 'High Utilization' : 'Normal'}
                </span>
              </div>
            </div>
          </div>

          {/* Capacity Overview Cards */}
          <div className="capacity-overview">
            <h2>Capacity Overview</h2>
            <div className="capacity-cards">
              <div className="capacity-card status-over">
                <h4>Over Capacity</h4>
                <p className="count">{analyticsData.capacityBreakdown?.routesOverCapacity || 0}</p>
                <span>routes</span>
              </div>
              <div className="capacity-card status-at">
                <h4>At Capacity</h4>
                <p className="count">{analyticsData.capacityBreakdown?.routesAtCapacity || 0}</p>
                <span>routes</span>
              </div>
              <div className="capacity-card status-optimal">
                <h4>Optimal</h4>
                <p className="count">{analyticsData.capacityBreakdown?.routesOptimal || 0}</p>
                <span>routes</span>
              </div>
              <div className="capacity-card status-under">
                <h4>Under Utilized</h4>
                <p className="count">{analyticsData.capacityBreakdown?.routesUnderUtilized || 0}</p>
                <span>routes</span>
              </div>
            </div>
          </div>

          {/* Charts Grid */}
          <div className="charts-grid">
            {capacityBreakdownChartData && (
              <div className="chart-card small">
                <h3>Capacity Distribution</h3>
                <Doughnut
                  data={capacityBreakdownChartData}
                  options={{
                    responsive: true,
                    maintainAspectRatio: true,
                  }}
                />
              </div>
            )}

            {routeCapacityChartData && (
              <div className="chart-card">
                <h3>Route Capacity vs Assigned Users</h3>
                <Bar
                  data={routeCapacityChartData}
                  options={{
                    responsive: true,
                    scales: {
                      x: {
                        ticks: {
                          maxRotation: 45,
                          minRotation: 45
                        }
                      }
                    }
                  }}
                />
              </div>
            )}

            {userDistributionChartData && (
              <div className="chart-card">
                <h3>User Distribution by Route</h3>
                <Bar
                  data={userDistributionChartData}
                  options={{
                    responsive: true,
                    indexAxis: 'y',
                  }}
                />
              </div>
            )}
          </div>

          {/* Route Details Table */}
          {analyticsData.routeAnalytics && analyticsData.routeAnalytics.length > 0 && (
            <div className="route-details-section">
              <h2>Route Details</h2>
              <div className="table-responsive">
                <table className="route-table">
                  <thead>
                    <tr>
                      <th>Route</th>
                      <th>Timing</th>
                      <th>Vehicles</th>
                      <th>Capacity</th>
                      <th>Assigned</th>
                      <th>Available</th>
                      <th>Utilization</th>
                      <th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {analyticsData.routeAnalytics.map((route, index) => (
                      <tr key={index}>
                        <td><strong>{route.routeName}</strong></td>
                        <td>{route.timing}</td>
                        <td>{route.assignedVehicles?.length || 0}</td>
                        <td>{route.totalCapacity}</td>
                        <td>{route.assignedUsers}</td>
                        <td className={route.availableSeats < 0 ? 'negative' : 'positive'}>
                          {route.availableSeats}
                        </td>
                        <td>{route.utilizationPercent?.toFixed(1)}%</td>
                        <td>
                          <span className={`status-badge ${getCapacityStatusColor(route.capacityStatus)}`}>
                            {getCapacityStatusLabel(route.capacityStatus)}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

export default VehicleDashboard;
